// src/lib/tvmaze.js
// Schedule accuracy layer on top of TMDB. TMDB air_date is a bare calendar date
// in the show origin country, with no time. TVmaze adds: a real network-local
// broadcast time (schedule.time), origin network + country, web-channel vs
// broadcast, and a next-episode airdate that is often fresher than TMDB.
//
// Mapping path: TMDB id -> TMDB external_ids (tvdb/imdb) -> TVmaze lookup -> show.
// Everything is cached in localStorage and all TVmaze calls are throttled, since
// the public API asks for no more than a request every ~fraction of a second.
import { tmdbGet } from './tmdb'
import { safeStorage } from './safeStorage'

const BASE = 'https://api.tvmaze.com'
const ID_KEY = 'nextep_tvmaze_ids'      // { tmdbId: { id: tvmazeId|null, t } }
const INFO_KEY = 'nextep_tvmaze_info'    // { tmdbId: { ...info, t } }
const ID_TTL = 30 * 24 * 3600 * 1000     // 30 days
const INFO_TTL = 12 * 3600 * 1000        // 12 hours

function loadMap(key) {
  try { return JSON.parse(safeStorage.get(key) || '{}') || {} } catch (e) { return {} }
}
function saveMap(key, m) {
  try { safeStorage.set(key, JSON.stringify(m)) } catch (e) {}
}

// Serialize TVmaze calls with a small gap so we stay a polite client.
let chain = Promise.resolve()
function throttled(path) {
  const run = async () => {
    await new Promise(r => setTimeout(r, 220))
    try {
      const res = await fetch(BASE + path)
      if (!res.ok) return null
      return await res.json()
    } catch (e) { return null }
  }
  const p = chain.then(run, run)
  chain = p.catch(() => {})
  return p
}

async function resolveId(tmdbId) {
  const ids = loadMap(ID_KEY)
  const hit = ids[tmdbId]
  if (hit && (Date.now() - hit.t) < ID_TTL) return hit.id
  let tvmazeId = null
  try {
    const ext = await tmdbGet('/tv/' + tmdbId + '/external_ids')
    const tvdb = ext && ext.tvdb_id
    const imdb = ext && ext.imdb_id
    let show = null
    if (tvdb) show = await throttled('/lookup/shows?thetvdb=' + tvdb)
    if (!show && imdb) show = await throttled('/lookup/shows?imdb=' + imdb)
    if (show && show.id) tvmazeId = show.id
  } catch (e) { tvmazeId = null }
  ids[tmdbId] = { id: tvmazeId, t: Date.now() }
  saveMap(ID_KEY, ids)
  return tvmazeId
}

// Round-hour UTC airstamps (e.g. T12:00:00+00:00) are TVmaze placeholders used
// when the real time is unknown, so the trustworthy time signal is schedule.time.
function pickTime(scheduleTime) {
  if (typeof scheduleTime === 'string' && /^[0-2]?[0-9]:[0-5][0-9]$/.test(scheduleTime)) return scheduleTime
  return ''
}

// A round-hour UTC airstamp (e.g. T12:00:00+00:00) is a TVmaze PLACEHOLDER — it means
// the real airtime, and often the real DATE, is unknown, so TVmaze parked the episode at
// noon UTC. In that state TVmaze's `airdate` is no fresher than TMDB's and can land a day
// off for an evening-US streaming drop (9pm ET Thu → next-day UTC), which silently pushed
// tonight's episodes into "tomorrow" and out of the Tonight lineup (Jason 2026-07-16:
// Silo/Sugar/Cape Fear read as airing the 17th when they actually drop the 16th). When the
// stamp is a placeholder we DON'T trust TVmaze's date over TMDB's.
export function isPlaceholderAirstamp(airstamp) {
  return typeof airstamp === 'string' && /T\d\d:00:00\+00:00$/.test(airstamp)
}

// Returns null if no TVmaze match, otherwise a normalized info object.
export async function getTvmazeInfo(tmdbId) {
  if (!tmdbId) return null
  const cache = loadMap(INFO_KEY)
  const c = cache[tmdbId]
  if (c && (Date.now() - c.t) < INFO_TTL) return c.v
  const tvmazeId = await resolveId(tmdbId)
  if (!tvmazeId) { cache[tmdbId] = { v: null, t: Date.now() }; saveMap(INFO_KEY, cache); return null }
  const show = await throttled('/shows/' + tvmazeId + '?embed=nextepisode')
  if (!show) return (c ? c.v : null)
  const net = show.network || null
  const web = show.webChannel || null
  const src = net || web || {}
  const country = (src.country && src.country.code) || ''
  const sch = show.schedule || {}
  const nx = (show._embedded && show._embedded.nextepisode) || null
  const info = {
    tvmazeId,
    networkName: src.name || '',
    countryCode: country,
    isWebChannel: !!web,
    scheduleTime: pickTime(sch.time),
    scheduleDays: sch.days || [],
    // Suppress TVmaze's airdate when the stamp is a placeholder, so callers fall back to
    // TMDB's air_date instead of a date that may be a day off (see isPlaceholderAirstamp).
    next: nx ? { airdate: isPlaceholderAirstamp(nx.airstamp) ? null : (nx.airdate || null), airstamp: nx.airstamp || null, season: nx.season, episode: nx.number } : null,
  }
  cache[tmdbId] = { v: info, t: Date.now() }
  saveMap(INFO_KEY, cache)
  return info
}

// 24h HH:MM (network local) -> friendly h:mm AM/PM. Returns '' if not parseable.
export function prettyTime(hhmm) {
  if (!hhmm) return ''
  const parts = hhmm.split(':')
  let h = parseInt(parts[0], 10)
  const m = parts[1] || '00'
  if (isNaN(h)) return ''
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12; if (h === 0) h = 12
  return h + ':' + m + ' ' + ampm
}
